import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InputWithLabel } from '@/components/ui/input';
import { TextareaWithLabel } from '@/components/ui/textarea';
import { ButtonLoading } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AutocompleteStyled, Option } from '@/components/atoms/inputs/autocomplete';
import type { CityKnowledge, AvailableCity } from '@/graphql/types/chatKnowledge';

interface AddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CityKnowledgeFormValues) => Promise<void>;
  loading: boolean;
  availableCities: AvailableCity[];
  initialData?: CityKnowledge | null;
}

export interface CityKnowledgeFormValues {
  title: string;
  content: string;
  category: string;
  municipality_code: string;
}

const validationSchema = yup.object().shape({
  title: yup.string().required('Título é obrigatório'),
  content: yup
    .string()
    .required('Conteúdo é obrigatório')
    .min(10, 'Conteúdo deve ter pelo menos 10 caracteres'),
  category: yup.string().required('Categoria é obrigatória'),
  municipality_code: yup.string().required('Município é obrigatório'),
});

export const AddDialog = ({
  open,
  onOpenChange,
  onSubmit,
  loading,
  availableCities,
  initialData,
}: AddDialogProps) => {
  const isEditMode = Boolean(initialData);

  const initialValues = useMemo<CityKnowledgeFormValues>(
    () => ({
      title: initialData?.title || '',
      content: initialData?.content || '',
      category: initialData?.category || '',
      municipality_code: initialData?.municipality_code || '',
    }),
    [initialData?.id, initialData?.title, initialData?.content, initialData?.category, initialData?.municipality_code]
  );

  // Valid category values and mapping - memoized outside render
  const validCategories = useMemo(() => ['general', 'history', 'economy', 'culture', 'tourism', 'other'], []);
  const categoryMapping = useMemo<Record<string, string>>(
    () => ({
      'geral': 'general',
      'história': 'history',
      'historia': 'history',
      'economia': 'economy',
      'cultura': 'culture',
      'turismo': 'tourism',
      'outro': 'other',
    }),
    []
  );

  // Create a Set for fast municipality lookup (O(1) instead of O(n))
  const municipalityCodeSet = useMemo(
    () => new Set(availableCities.map((city) => String(city.municipality_code))),
    [availableCities]
  );

  // Convert availableCities to Autocomplete options format - memoized
  const municipalityOptions = useMemo<Option[]>(
    () =>
      availableCities.map((city) => ({
        label: `${city.name_municipality} - ${city.sigla_uf}`,
        value: city.municipality_code,
      })),
    [availableCities]
  );

  // Find the selected municipality option
  const getSelectedMunicipalityOption = useCallback(
    (municipalityCode: string | undefined): Option | null => {
      if (!municipalityCode) return null;
      const codeStr = String(municipalityCode);
      const option = municipalityOptions.find((opt) => String(opt.value) === codeStr);
      return option || null;
    },
    [municipalityOptions]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6">
        <DialogHeader className="w-fit">
          <DialogTitle>{isEditMode ? 'Editar' : 'Adicionar'} Conhecimento</DialogTitle>
        </DialogHeader>

        <Formik
          key={`${initialData?.id || 'new'}-${initialData?.category || ''}-${initialData?.municipality_code || ''}`}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          enableReinitialize={true}
        >
          {({ values, errors, setFieldValue }) => {
            // Normalize and map category value
            let categoryValue: string | undefined = undefined;
            if (values.category && values.category.trim() !== '') {
              const normalized = values.category.trim().toLowerCase();
              const mapped = categoryMapping[normalized] || normalized;
              categoryValue = validCategories.includes(mapped) ? mapped : undefined;
            }

            // Check municipality existence using Set for O(1) lookup
            let municipalityValue: string | undefined = undefined;
            if (values.municipality_code && values.municipality_code.toString().trim() !== '') {
              const codeStr = String(values.municipality_code);
              municipalityValue = municipalityCodeSet.has(codeStr) ? codeStr : undefined;
            }

            return (
            <Form className="flex flex-col gap-6 w-full">
              <InputWithLabel
                label="Título"
                id="title"
                name="title"
                value={values.title}
                onChange={(event) => setFieldValue('title', event.target.value)}
                error={Boolean(errors.title)}
                helperText={errors.title}
                className="w-full max-w-full"
              />

              <div className="flex flex-col gap-2 w-full max-w-full">
                <Label htmlFor="category">Categoria</Label>
                <Select
                  key={`category-${categoryValue || 'empty'}-${initialData?.id || 'new'}`}
                  name="category"
                  value={categoryValue}
                  onValueChange={(value) => setFieldValue('category', value)}
                >
                  <SelectTrigger className="h-8 w-full">
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent side="bottom">
                    <SelectItem value="general">Geral</SelectItem>
                    <SelectItem value="history">História</SelectItem>
                    <SelectItem value="economy">Economia</SelectItem>
                    <SelectItem value="culture">Cultura</SelectItem>
                    <SelectItem value="tourism">Turismo</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-red-500 text-xs">{errors.category}</p>
                )}
              </div>

              <div className="flex flex-col gap-2 w-full max-w-full">
                <Label htmlFor="municipality_code">Município</Label>
                <AutocompleteStyled
                  id="municipality_code"
                  name="municipality_code"
                  label=""
                  placeholder="Selecione o município"
                  options={municipalityOptions}
                  value={getSelectedMunicipalityOption(municipalityValue)}
                  onChange={(_, option) => {
                    setFieldValue('municipality_code', option?.value ? String(option.value) : '');
                  }}
                  margin="none"
                  fullWidth={true}
                  helperText={errors.municipality_code}
                  required={true}
                />
              </div>

              <TextareaWithLabel
                label="Conteúdo"
                id="content"
                name="content"
                rows={10}
                value={values.content}
                onChange={(event) => setFieldValue('content', event.target.value)}
                error={Boolean(errors.content)}
                helperText={errors.content}
                className="w-full max-w-full"
              />

              <DialogFooter className="flex-col">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button>

                <ButtonLoading
                  variant="default"
                  label={isEditMode ? 'Salvar alterações' : 'Adicionar'}
                  loading={loading}
                  type="submit"
                  disabled={
                    loading ||
                    Boolean(errors.title) ||
                    Boolean(errors.content) ||
                    Boolean(errors.category) ||
                    Boolean(errors.municipality_code)
                  }
                />
              </DialogFooter>
            </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
